import { Alert, InputNumber, Popover, Space, Tooltip, Typography } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import VSpace from '../common/VSpace'
import DownPayment from './DownPayment'
import PaymentSchedule from './PaymentSchedule'
import InvoiceRateSelector from './PaymentOptions/InvoiceRateSelector'
import PaymentMethods, { PaymentMethodOnFileSettings } from './PaymentOptions/PaymentMethods'
import SwitchWithDescription from '../common/SwitchWithDescription'
import SettingsTitledCard from '../settings/SettingsTitledCard'
import RadioOption from './PaymentOptions/RadioOption'
import SynkedUPPaymentsBanner from '../invoicing/SynkedUPPaymentsBanner'
import { PricingTypes } from '../../common/constants'
import {
  Action,
  calculateTotalsForPaymentGroups,
  findUnlinkedCostCodes,
  ProposalCreationParameters,
  setEstimateSalesTax,
  setIncludePricingType,
  setShowCompanyAddress,
  setRedirectToDownPaymentCheckoutOnApproval
} from './proposalsModel'

type Props = {
  model: ProposalCreationParameters
  dispatch: (action: Action) => void
  onLinkCustomerToQbo: (shouldSave: boolean) => void
  onLinkCostCodeToQbo: (costCodeId: number, shouldSave: boolean) => void
}

const ManageProposalPayments = ({ model, dispatch, onLinkCustomerToQbo, onLinkCostCodeToQbo }: Props) => {
  const { lineItems, downPayment, includePricingTypeInformation, showCompanyAddress, redirectToDownPaymentCheckoutOnApproval, paymentScheduleOptions } = model
  const isQuickBooks = downPayment.invoicingSystem === 'quickBooks'
  const estimateSalesTaxDisabled = isQuickBooks && downPayment.isCustomerLinkedToQbo !== true
  const totals = calculateTotalsForPaymentGroups(lineItems)
  const includesTimeAndMaterialsOrFlatRate = lineItems.some(x => x.enabled
    && (x.pricingType === PricingTypes.TimeAndMaterials || x.pricingType === PricingTypes.FlatRate))
  const selectedOption = paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption
  const showRedirectOption = downPayment.invoicingSystem === 'synkedUp'
  const redirectDisabled = downPayment.autoSubmitDownPaymentInvoice === true && selectedOption === 'progress-based'

  const autoSubmit = <DownPayment
    downPayment={{ ...downPayment, costCodesUnlinkedToQbo: findUnlinkedCostCodes(lineItems) }}
    lineItems={lineItems}
    onLinkCustomerToQbo={onLinkCustomerToQbo}
    onLinkCostCodeToQbo={onLinkCostCodeToQbo}
    dispatch={dispatch}
    includesTimeAndMaterialsOrFlatRate={includesTimeAndMaterialsOrFlatRate}
  />

  return <VSpace>
    <SynkedUPPaymentsBanner />

    {totals.quotedAndTimeAndMaterials > 0 && <SettingsTitledCard
      title="Invoicing Schedule for Quoted and Time & Material"
      description="Choose when this work is invoiced and how the total is split into payments."
    >
      <VSpace>
        <RadioOption
          label="Paid Upfront"
          description="Invoice the full amount as soon as the proposal is approved"
          checked={selectedOption === 'paid-upfront'}
          onClick={() => dispatch({ type: 'selectQuotedAndTimeAndMaterialsPaymentOption', value: 'paid-upfront' })}
        />
        <RadioOption
          label="Progress-based"
          description="Pay by completion milestone ie: 20% upfront, 80% on completion"
          checked={selectedOption === 'progress-based'}
          onClick={() => dispatch({ type: 'selectQuotedAndTimeAndMaterialsPaymentOption', value: 'progress-based' })}
        />
        <RadioOption
          label="Interval-based"
          description="Equally distribute total across your chosen set of payments"
          checked={selectedOption === 'interval-based'}
          onClick={() => dispatch({ type: 'selectQuotedAndTimeAndMaterialsPaymentOption', value: 'interval-based' })}
        />

        {selectedOption === 'paid-upfront' && autoSubmit}

        {selectedOption === 'progress-based' && <VSpace>
          {autoSubmit}
          <PaymentSchedule
            paymentSchedule={paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased}
            total={totals.quotedAndTimeAndMaterials}
            dispatch={dispatch}
          />
        </VSpace>}

        {selectedOption === 'interval-based' && <VSpace>
          <Space>
            Total number of payments
            <InputNumber
              value={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments}
              min={1}
              max={99}
              onChange={value => dispatch({ type: 'updateIntervalBasedTotalPayments', value: value ?? 1 })}
            />
          </Space>
          <InvoiceRateSelector
            recurringPayment={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.recurring}
            totalAmount={totals.quotedAndTimeAndMaterials}
            totalPayments={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments}
            onChange={value => dispatch({ type: 'updateIntervalBasedRecurringPayment', value })}
            showEstimated={includesTimeAndMaterialsOrFlatRate}
          />
        </VSpace>}
      </VSpace>
    </SettingsTitledCard>}

    {totals.perVisit > 0 && <SettingsTitledCard
      title="Invoicing Schedule for Per Visit"
      description="Choose how often per-visit work is invoiced."
    >
      <InvoiceRateSelector
        recurringPayment={paymentScheduleOptions.perVisit.recurring}
        totalAmount={totals.perVisit}
        onChange={value => dispatch({ type: 'updatePerVisitRecurringPayment', value })}
      />
    </SettingsTitledCard>}

    <SettingsTitledCard
      title="Payment Methods"
      description="Choose how your customer can pay this proposal."
    >
      <PaymentMethods downPayment={downPayment} dispatch={dispatch} redesign />
    </SettingsTitledCard>

    {downPayment.invoicingSystem === 'synkedUp' && <SettingsTitledCard
      title="Payment Method on File Settings"
      description="Set the behavior for how customers see and use the payment authorization option during checkout."
    >
      <PaymentMethodOnFileSettings downPayment={downPayment} dispatch={dispatch} redesign />
    </SettingsTitledCard>}

    <SettingsTitledCard
      title="General Settings"
      description="Control what your customer sees on the proposal and what happens after they approve it."
    >
      <VSpace>
        <SwitchWithDescription
          checked={includePricingTypeInformation}
          label="Include Pricing Type in Customer Proposal"
          description="Display whether pricing is fixed, time & materials, or another type directly in the customer proposal."
          onClick={() => dispatch(setIncludePricingType(!includePricingTypeInformation))}
        />
        <SwitchWithDescription
          checked={showCompanyAddress}
          label="Show Company Address in Customer Proposal"
          description="Display your company address on the customer proposal and the PDF."
          onClick={() => dispatch(setShowCompanyAddress(!showCompanyAddress))}
        />
        <Space align="start">
          <SwitchWithDescription
            checked={model.estimateSalesTax}
            label="Estimate Sales Tax in Customer Proposal"
            description="Include an estimated sales tax amount on the customer proposal so they can see the total cost upfront."
            disabled={estimateSalesTaxDisabled}
            onClick={() => dispatch(setEstimateSalesTax(!model.estimateSalesTax))}
          />
          {isQuickBooks && <Popover content={<div style={{ width: '350px' }}>
            Sales tax settings in your QuickBooks Online account must be set up and working to successfully show
            estimated sales tax on your proposals. If we cannot create the estimate in QuickBooks for any reason,
            the estimate will not include estimated sales tax.
          </div>}>
            <InfoCircleOutlined />
          </Popover>}
        </Space>
        {estimateSalesTaxDisabled && <Alert
          type="warning"
          showIcon
          message="Sales Tax Estimation Requires QuickBooks Link"
          description={<Typography.Link onClick={() => onLinkCustomerToQbo(false)}>Link Customer</Typography.Link>}
        />}
        {showRedirectOption && <div>
          <Tooltip title={redirectDisabled ? 'Not available when auto-submitting a down-payment invoice.' : undefined}>
            <SwitchWithDescription
              checked={redirectToDownPaymentCheckoutOnApproval}
              label="Redirect Customer to Payment after Proposal Approval"
              description="If a proposal includes an approved quoted work area, customers will be taken directly to make their down payment after approval."
              disabled={redirectDisabled}
              onClick={() => dispatch(setRedirectToDownPaymentCheckoutOnApproval(!redirectToDownPaymentCheckoutOnApproval))}
            />
          </Tooltip>
        </div>}
      </VSpace>
    </SettingsTitledCard>
  </VSpace>
}

export default ManageProposalPayments
